// A job's one page (#/jobs/<id>, BL-128): reads the job once (jobs.page), draws it with the job page
// (@flowology/ui-web/job's JobPage, from job-view-model.ts's view), and runs what the page hands back
// with the server's own actions, reading the job again after each and whenever a live change touches
// it. Loading, failed and not found are the page's own. What the new page doesn't draw itself (an
// estimate's editor, the invoice's lines, the trip charge on a cancel, the receipts, every file) opens
// in a dialog over it with the piece the old sheets used, so nothing the job sheet or Review could do
// is lost. Every control shows only where jobs.page's `can` said so. Every write is one attempt with one
// idempotency key (job-writes.ts), so trying it again after a lost answer does it once (QA-058).
// In Blueprint it draws round 2's job record (JobRecord, from job-record-model.ts, approved 26 Oct),
// whose History rows open in place and ride in the page's address (?open=…); Riverstone keeps JobPage.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { localDateOf } from '@flowology/kernel';
import { Button, ConfirmDialog, DialogFrame, Layer } from '@flowology/ui-web/fl';
import { JobPage, JobRecord, JobVisitBlock, SEND_BACK_REASONS, type JobAction } from '@flowology/ui-web/job';
import { useDesign } from '../blueprint/design.ts';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { AddVisit } from '../shared/AddVisit.tsx';
import { PoNumber } from '../shared/PoNumber.tsx';
import { JOB_PAGE_WAIT_MS, followLive, jobSheetFollows } from '../shared/sheet-live.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { attemptKeys } from '../shared/attempt-keys.ts';
import { TaskList } from '../tasks/TaskList.tsx';
import { InvoiceLines } from '../review/InvoiceLines.tsx';
import { InvoiceActions } from '../review/InvoiceActions.tsx';
import { KeepCredit } from '../review/KeepCredit.tsx';
import type { EditableInvoice } from '../review/invoice-lines-model.ts';
import { ReopenJob } from '../history/CustomerEdit.tsx';
import { EstimateDeposits } from './Deposits.tsx';
import { signedTotal } from './deposits-model.ts';
import { EstimateEditor } from './EstimateEditor.tsx';
import { EstimateBuild } from './EstimateBuild.tsx';
import { CancelDialog, CancelVisitDialog, EstimateCard, SendDialog } from './job-dialogs.tsx';
import { JobPhotos } from './JobPhotos.tsx';
import { JobPurchases } from './JobPurchases.tsx';
import { jobNoted } from './noted.ts';
import { openJob, openRowsOf, withOpenRows } from './job-page.ts';
import { openRowsIn, toJobRecordView } from './job-record-model.ts';
import { jobSiteMap } from './site-map.tsx';
import { Splits } from './Splits.tsx';
import type { Estimate } from './job-model.ts';
import { textablePhone, toJobView, type JobPageData } from './job-view-model.ts';
import { writeOf, type Write } from './job-writes.ts';
import './job-route.css';

export interface JobRouteProps {
  jobId: string;
  /** The person's permissions, for the old sheets' pieces that check their own. */
  held: readonly string[];
  /** The company's. */
  timeZone: string;
  /** The place the page was opened from, named over the job, and the way back to it. */
  crumb: { label: string; onPress: () => void };
  onClose: () => void;
  /** Book the work an estimate sold for later (Book, started from the estimate). */
  onBookEstimate: (estimate: { customerId: string; id: string; number: string; jobId: string }) => void;
  /** Inbox › Messages with only this job's texts and emails. */
  onOpenMessages: (job: { id: string; number: string }) => void;
  onOpenCustomer: (customerId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
  /** The board on a day, with one visit picked. */
  onBoard: (date: string, visit?: string) => void;
  /** Something on the job changed: places showing it may read it again. */
  onChanged?: () => void;
  /** The board's visit the page was opened from. */
  fromVisit?: string | null;
}

/** What's open over the page. */
type Over =
  | { kind: 'addvisit' }
  | { kind: 'cancel' }
  | { kind: 'cancel-visit'; id: string; when: string }
  | { kind: 'estimate-edit'; start: Estimate | 'new' }
  | { kind: 'estimate-open'; id: string }
  | { kind: 'estimate-send'; id: string; estimate?: Estimate }
  | { kind: 'deposits'; id: string }
  | { kind: 'lines' }
  | { kind: 'invoice-send' }
  | { kind: 'void' }
  | { kind: 'reopen' }
  | { kind: 'splits' }
  | { kind: 'po' }
  | { kind: 'receipts' }
  | { kind: 'gallery' }
  | { kind: 'tasks' }
  | { kind: 'why' }
  | { kind: 'text'; methodId: string };

/** One of the old sheets' pieces in a dialog over the page. */
function Panel({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <Layer level="dialog" size="lg" label={title} onClose={onClose}>
      <DialogFrame title={title} footer={<Button onPress={onClose}>Done</Button>}>
        <div className="jobroute-panel">{children}</div>
      </DialogFrame>
    </Layer>
  );
}

export function JobRoute({
  jobId,
  held,
  timeZone,
  crumb,
  onClose,
  onBookEstimate,
  onOpenMessages,
  onOpenCustomer,
  onOpenInvoice,
  onBoard,
  onChanged,
  fromVisit: fromVisitProp = null,
}: JobRouteProps) {
  const [data, setData] = useState<JobPageData | null>(null);
  const [status, setStatus] = useState<'loading' | 'failed' | 'notfound' | 'ready'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; isError?: boolean } | null>(null);
  const [over, setOver] = useState<Over | null>(null);
  const [busy, setBusy] = useState(false);
  const mirroring = useMirror();
  const design = useDesign();
  // only Blueprint's record opens on the visit it came from; Riverstone's page stays as it was
  const fromVisit = design === 'blueprint' ? fromVisitProp : null;
  const drawn = useRef(false);
  // Blueprint's record: the rows open in place (visits, forms, History), as the address has them (a
  // copied link, Back)
  const [rowsOpen, setRowsOpen] = useState<string[]>(() => openRowsOf(location.hash));
  const onOpenRows = (ids: string[]) => {
    setRowsOpen(ids);
    history.replaceState(history.state, '', withOpenRows(location.hash, ids));
  };
  // Back or a pasted link to the same job with other rows open
  useEffect(() => {
    const onHash = () => setRowsOpen(openRowsOf(location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  // the skeleton says "Still loading" after ten seconds
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (status !== 'loading') return setSlow(false);
    const t = setTimeout(() => setSlow(true), 10_000);
    return () => clearTimeout(t);
  }, [status]);
  // one idempotency key per attempt at each write, kept across its retries (QA-058, QA-744)
  const [keys] = useState(attemptKeys);
  // Complete never says the customer is asked to rate the job while texting isn't set up (QA-324)
  const [textingOff, setTextingOff] = useState(false);
  const mayComplete = !!data?.can.complete;
  useEffect(() => {
    if (!mayComplete) return;
    let live = true;
    void client
      .call<{ sms: boolean }>('messages.channels', {})
      .then((c) => live && setTextingOff(!c.sms))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [mayComplete]);

  const load = useCallback(async () => {
    try {
      const d = await client.call<JobPageData>('jobs.page', { id: jobId });
      drawn.current = true;
      setData(d);
      setStatus('ready');
      setError(null);
    } catch (e) {
      if (e instanceof RpcError && e.code === 'not_found') return setStatus('notfound');
      // a page already drawn keeps what it had and says the read failed
      if (drawn.current) setNotice({ text: errorText(e, 'Couldn’t read the job again.'), isError: true });
      else {
        setStatus('failed');
        setError(errorText(e, 'The job didn’t load.'));
      }
    }
  }, [jobId]);
  useEffect(() => {
    void load();
  }, [load]);

  // Left open while the customer signs or pays, it follows the live changes to the job, its visits,
  // estimates and invoices, and money moving (QA-728); and its texts and emails, so a customer's reply
  // shows as it arrives (QA-743).
  const records = useRef<Set<string>>(new Set());
  records.current = new Set([
    jobId,
    ...(data?.job.appointments.map((a) => a.id) ?? []),
    ...(data?.estimates?.estimates.map((e) => e.id) ?? []),
    ...(data?.more.invoices.map((i) => i.id) ?? []),
    ...(data?.job.signatures?.map((s) => s.invoiceId) ?? []),
    ...(data?.messages?.messages.map((m) => m.id) ?? []),
  ]);
  useEffect(
    () =>
      followLive(
        (c) => jobSheetFollows(c, records.current),
        () => void load(),
        JOB_PAGE_WAIT_MS,
      ),
    [load],
  );

  useEffect(() => {
    if (!notice || notice.isError) return;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const view = useMemo(
    () =>
      data
        ? toJobView(data, {
            timeZone,
            now: new Date(),
            crumb: crumb.label,
            fromVisit,
            mirroring,
            textingOff,
            ...(notice ? { notice } : mirroring ? { notice: { text: MIRROR_LINE } } : {}),
          })
        : null,
    [data, timeZone, crumb.label, fromVisit, mirroring, textingOff, notice],
  );
  const record = useMemo(
    () =>
      design === 'blueprint' && view && data
        ? toJobRecordView(view, data, { timeZone, now: new Date(), crumb: crumb.label, fromVisit, mirroring })
        : null,
    [design, view, data, timeZone, crumb.label, fromVisit, mirroring],
  );

  /** Said once it's done, read again, and told to whoever else shows the job. */
  const changed = async (said: string) => {
    setNotice({ text: said });
    await load();
    onChanged?.();
  };
  /**
   * Does it with the attempt's key and says so; a refusal is said on the page and handed back (a dialog
   * or the composer keeps it, and trying again sends the same key). Done, the next try is a new attempt.
   */
  const run = async (
    w: Write,
    fn: (idempotencyKey: string) => Promise<unknown>,
    said: string | ((answer: unknown) => string),
  ) => {
    setBusy(true);
    try {
      const answer = await fn(keys.key(w.attempt, w.signature));
      keys.end(w.attempt);
      await changed(typeof said === 'function' ? said(answer) : said);
    } catch (e) {
      setNotice({ text: errorText(e), isError: true });
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const onAction = async (a: JobAction): Promise<void> => {
    if (!data) {
      if (a.type === 'go' && a.id === 'close') onClose();
      if (a.type === 'go' && a.id === 'crumb') crumb.onPress();
      return;
    }
    const { job } = data;
    const n = job.number;
    const w = writeOf(a, data);
    switch (a.type) {
      case 'go':
        return go(a.id, a.target);
      case 'complete':
        return run(
          w!,
          (idempotencyKey) =>
            client.call(
              'jobs.complete',
              { jobId, expectedRowVersion: data.review?.rowVersion },
              { idempotencyKey },
            ),
          `Job ${n} is completed, and its invoice is finalized.`,
        );
      case 'sendBack': {
        const why = SEND_BACK_REASONS.find((r) => r.value === a.reason)?.label ?? 'Something else';
        return run(
          w!,
          (idempotencyKey) =>
            client.call(
              'jobs.sendBack',
              { jobId, reason: `${why}: ${a.message.trim()}` },
              { idempotencyKey },
            ),
          'Sent back. It comes back for review when the tech sends it in again.',
        );
      }
      case 'cancel':
        // the page's own cancel isn't offered: the old one waives the trip charge and texts the customer
        return setOver({ kind: 'cancel' });
      case 'editDetails':
        setNotice({ text: 'A job’s details can’t be changed here yet.', isError: true });
        throw new Error('A job’s details can’t be changed here yet.');
      case 'fix':
        return fix(w!, a.fixId, a.choice);
      case 'compose':
        return compose(w!, a.mode, a.text);
      case 'task': {
        const t = data.tasks?.tasks.find((x) => x.id === a.taskId);
        if (!t || !w) return;
        return run(
          w,
          (idempotencyKey) =>
            a.isDone
              ? client.call(
                  'tasks.close',
                  { id: t.id, rowVersion: t.rowVersion, outcome: 'done' },
                  { idempotencyKey },
                )
              : client.call(
                  'tasks.update',
                  { id: t.id, rowVersion: t.rowVersion, status: 'open' },
                  { idempotencyKey },
                ),
          a.isDone ? `Done: ${t.title}.` : `Open again: ${t.title}.`,
        );
      }
      case 'share':
        return run(
          w!,
          (idempotencyKey) =>
            client.call(
              'attachments.setVisibility',
              { attachmentId: a.photoId, visibility: a.shared ? 'customer' : 'internal' },
              { idempotencyKey },
            ),
          a.shared ? 'The customer sees the photo.' : 'Hidden from the customer: only the office sees it.',
        );
    }
  };

  const go = (id: string, target?: string) => {
    if (!data) return;
    const { job } = data;
    const est = (eid?: string) => data.estimates?.estimates.find((e) => e.id === eid);
    switch (id) {
      case 'close':
        return onClose();
      case 'crumb':
        return crumb.onPress();
      case 'copy':
        void navigator.clipboard
          ?.writeText(location.href)
          .then(() => setNotice({ text: 'The link to the job is copied.' }))
          .catch(() =>
            setNotice({ text: 'Couldn’t copy the link. Copy it from the address bar.', isError: true }),
          );
        return;
      case 'customer':
        return onOpenCustomer(job.customerId);
      case 'invoice':
        return onOpenInvoice(target ?? job.invoice?.id ?? '');
      case 'job':
        if (target) openJob(target);
        return;
      case 'messages':
        return onOpenMessages({ id: job.id, number: job.number });
      case 'board': {
        const visits = [...job.appointments].sort((x, y) => x.start.localeCompare(y.start));
        const v =
          visits.find((x) => x.id === target) ??
          visits.find((x) => x.status !== 'cancelled' && x.status !== 'closed') ??
          visits.at(-1);
        return onBoard(
          v ? localDateOf(new Date(v.start), timeZone) : localDateOf(new Date(), timeZone),
          v?.id,
        );
      }
      case 'text':
        return target ? setOver({ kind: 'text', methodId: target }) : undefined;
      case 'addvisit':
        return setOver({ kind: 'addvisit' });
      case 'cancel':
        return setOver({ kind: 'cancel' });
      case 'cancel-visit': {
        const v = view?.visits.rows.find((x) => x.id === target);
        return v ? setOver({ kind: 'cancel-visit', id: v.id, when: `${v.day}, ${v.window}` }) : undefined;
      }
      case 'estimate-new':
        return setOver({ kind: 'estimate-edit', start: 'new' });
      case 'estimate-edit': {
        const e = est(target);
        return e ? setOver({ kind: 'estimate-edit', start: e }) : undefined;
      }
      case 'estimate-open':
      case 'estimate-send':
      case 'deposits':
        return target ? setOver({ kind: id, id: target }) : undefined;
      case 'book': {
        const e = est(target);
        if (e) onBookEstimate({ customerId: job.customerId, id: e.id, number: e.number, jobId: e.jobId });
        return;
      }
      case 'tasks-add':
        return setOver({ kind: 'tasks' });
      case 'lines':
      case 'invoice-send':
      case 'void':
      case 'reopen':
      case 'splits':
      case 'po':
      case 'receipts':
      case 'gallery':
        return setOver({ kind: id });
      default:
        return;
    }
  };

  const fix = async (w: Write, fixId: string, choice: string) => {
    if (!data) return;
    if (fixId.startsWith('mat:')) {
      const m = data.review?.materials.find((x) => x.id === fixId.slice(4));
      if (!m) return;
      return run(
        w,
        (idempotencyKey) =>
          client.call('materials.review', { materialId: m.id, bill: choice === 'bill' }, { idempotencyKey }),
        choice === 'bill' ? `${m.name} is on the invoice.` : `${m.name} won’t be billed.`,
      );
    }
    if (fixId.startsWith('flag:')) {
      const f = data.review?.flags.find((x) => x.id === fixId.slice(5));
      if (!f) return;
      if (choice === 'hide' || choice === 'show')
        return run(
          w,
          (idempotencyKey) =>
            client.call(
              'attachments.setVisibility',
              { attachmentId: f.entityId, visibility: choice === 'hide' ? 'internal' : 'customer' },
              { idempotencyKey },
            ),
          choice === 'hide' ? 'Hidden from the customer.' : 'The customer still sees it.',
        );
      return run(
        w,
        (idempotencyKey) =>
          client.call(
            'reviewFlags.resolve',
            { flagId: f.id, resolution: choice === 'dismissed' ? 'dismissed' : 'resolved' },
            { idempotencyKey },
          ),
        choice === 'dismissed' ? 'Flag dismissed.' : 'Flag resolved.',
      );
    }
    if (fixId === 'signature') return setOver({ kind: 'why' });
    if (fixId === 'po') return setOver({ kind: 'po' });
  };

  const compose = async (w: Write, mode: string, text: string) => {
    if (!data) return;
    const body = text.trim();
    // a note's and a techs' message's own id is the attempt's key: sent again, it's added once
    if (mode === 'note')
      return run(
        w,
        async (key) => {
          await client.call('notes.add', { noteId: key, jobId, body }, { idempotencyKey: key });
          jobNoted();
        },
        'Note added.',
      );
    if (mode === 'techs')
      return run(
        w,
        (key) => client.call('jobMessages.send', { id: key, jobId, body }, { idempotencyKey: key }),
        'Sent to the techs.',
      );
    if (mode === 'text') {
      const phone = textablePhone(data.customer);
      if (!phone) throw new Error('There’s no number that agreed to texts.');
      return run(
        { ...w, signature: `${phone.id}:${w.signature}` },
        (idempotencyKey) =>
          client.call(
            'messages.send',
            { contactMethodId: phone.id, purpose: 'general', body, jobId },
            { idempotencyKey },
          ),
        'Text sent.',
      );
    }
  };

  if ((status !== 'ready' || !view || !data) && design === 'blueprint')
    return (
      <JobRecord
        waiting={{
          status: status === 'ready' ? 'loading' : status === 'loading' && slow ? 'slow' : status,
          crumb: { id: 'crumb', label: crumb.label },
          ...(error ? { error } : {}),
        }}
        onRetry={() => {
          setStatus('loading');
          void load();
        }}
        onAction={onAction}
      />
    );
  if (status !== 'ready' || !view || !data)
    return (
      <JobPage
        status={status === 'ready' ? 'loading' : status}
        crumb={{ id: 'crumb', label: crumb.label }}
        {...(error ? { error } : {})}
        onRetry={() => {
          setStatus('loading');
          void load();
        }}
        visitBlock={(v) => <JobVisitBlock v={v} />}
        onAction={onAction}
      />
    );

  const { job, can } = data;
  // closing a dialog ends its attempt: opened again, it's a new one with a new key
  const shut = () => {
    if (over) keys.end(over.kind);
    setOver(null);
  };
  const done = (said: string) => {
    setOver(null);
    void changed(said);
  };
  const est = (id: string) => data.estimates?.estimates.find((e) => e.id === id);
  const names = new Map(data.more.visits.flatMap((v) => v.techs.map((t) => [t.staffId, t.name] as const)));
  const inv = job.invoice;

  let layer: ReactNode = null;
  if (over)
    switch (over.kind) {
      case 'addvisit':
        layer = (
          <Panel title={`Add a visit to job ${job.number}`} onClose={shut}>
            <p>
              A return visit on this job (it needs parts, or more work), not a new job. The board dispatches
              it.
            </p>
            <AddVisit
              jobId={job.id}
              jobNumber={job.number}
              leadTechId={job.appointments.at(-1)?.leadTechId ?? job.appointments.at(-1)?.techIds[0] ?? null}
              timeZone={timeZone}
              onAdded={done}
            />
          </Panel>
        );
        break;
      case 'cancel':
        layer = <CancelDialog job={job} timeZone={timeZone} onClose={shut} onCancelled={done} />;
        break;
      case 'cancel-visit':
        layer = (
          <CancelVisitDialog
            job={job}
            visit={{ id: over.id, when: over.when }}
            onClose={shut}
            onCancelled={done}
          />
        );
        break;
      case 'estimate-edit':
        layer = (
          <Panel
            title={over.start === 'new' ? 'New estimate' : `Estimate ${over.start.number}`}
            onClose={shut}
          >
            <EstimateEditor
              job={job}
              start={over.start}
              onCancel={shut}
              onSaved={(e, isNew) => done(`${isNew ? 'Saved' : 'Updated'} estimate ${e.number}.`)}
            />
          </Panel>
        );
        break;
      case 'estimate-open': {
        const e = est(over.id);
        if (e)
          layer = (
            <Panel title={`Estimate ${e.number}`} onClose={shut}>
              <EstimateCard
                estimate={e}
                held={held}
                mirroring={mirroring}
                jobLive={job.status !== 'cancelled'}
                unbooked={(data.soldNotBooked ?? []).includes(e.id)}
                onEdit={() => setOver({ kind: 'estimate-edit', start: e })}
                onSend={() => setOver({ kind: 'estimate-send', id: e.id })}
                onBook={() =>
                  onBookEstimate({ customerId: job.customerId, id: e.id, number: e.number, jobId: e.jobId })
                }
                onApproved={(o) => void changed(`Price OK’d on “${o}”. It can be signed now.`)}
              />
            </Panel>
          );
        break;
      }
      case 'estimate-send': {
        // just saved from the builder: the job's estimates read again, so it's the one saved till then
        const e = est(over.id) ?? over.estimate;
        if (e)
          layer = (
            <SendDialog
              estimate={e}
              customerId={job.customerId}
              onClose={shut}
              onSent={(count) =>
                // queued, not gone: the estimate reads Sent once a message of it has (QA-871)
                done(
                  `Sending estimate ${e.number} to ${count === 1 ? 'the customer' : `${count} contacts`}: it reads Sent once it’s gone.`,
                )
              }
            />
          );
        break;
      }
      case 'deposits': {
        const e = est(over.id);
        if (e)
          layer = (
            <Panel title={`Deposits on estimate ${e.number}`} onClose={shut}>
              <EstimateDeposits
                estimateId={e.id}
                estimateNumber={e.number}
                customerName={job.customerName}
                totalCents={signedTotal(e)}
                mayTake={!!can.takeDeposit && !mirroring}
                onTaken={(said) => void changed(said)}
              />
            </Panel>
          );
        break;
      }
      case 'lines':
        if (inv)
          layer = (
            <Panel title={`Invoice ${inv.number}’s lines`} onClose={shut}>
              {/* jobs.get's invoice carries what the line editor reads */}
              <InvoiceLines
                jobId={job.id}
                invoice={inv as unknown as EditableInvoice}
                rights={{ lines: !!can.editInvoice && !mirroring }}
                onChanged={() => void changed('The invoice is changed.')}
              />
              {!!can.editInvoice && !mirroring && (
                <KeepCredit invoice={inv} onDone={() => void changed('Kept as credit.')} />
              )}
            </Panel>
          );
        break;
      case 'invoice-send':
        if (inv)
          layer = (
            <Panel title={`Invoice ${inv.number}`} onClose={shut}>
              <InvoiceActions
                invoice={inv}
                onSent={() => void changed(`Invoice ${inv.number} is on its way.`)}
              />
            </Panel>
          );
        break;
      case 'void':
        if (inv)
          layer = (
            <ConfirmDialog
              isOpen
              onClose={shut}
              title={`Void invoice ${inv.number}?`}
              confirmLabel="Void the invoice"
              cancelLabel="Keep it"
              reason={{
                label: 'Why is it void?',
                description: 'For the job’s history. The customer doesn’t see it.',
                missing: 'Say why, so the next person to open the job knows.',
              }}
              onConfirm={(reason) =>
                run(
                  { attempt: 'void', signature: reason },
                  (idempotencyKey) =>
                    client.call('invoices.void', { invoiceId: inv.id, reason }, { idempotencyKey }),
                  (answer) =>
                    [`Invoice ${inv.number} is void.`, (answer as { message?: string } | null)?.message]
                      .filter(Boolean)
                      .join(' '),
                ).then(shut)
              }
            >
              <p>It stays on the job, marked void. A payment on it stays with the customer as credit.</p>
            </ConfirmDialog>
          );
        break;
      case 'reopen':
        layer = <ReopenJob job={{ id: job.id, title: `#${job.number}` }} onClose={shut} onSaved={done} />;
        break;
      case 'splits':
        if (data.review)
          layer = (
            <Panel title="Who shares the credit" onClose={shut}>
              <Splits
                key={`${data.review.rowVersion}`}
                initial={data.review.splits}
                source={data.review.splitsSource}
                name={(id) => names.get(id) ?? 'A tech'}
                busy={busy}
                onSave={async (splits) => {
                  try {
                    await run(
                      { attempt: 'splits', signature: JSON.stringify(splits) },
                      (idempotencyKey) =>
                        client.call('jobs.setSplits', { jobId, splits }, { idempotencyKey }),
                      'Shares saved.',
                    );
                    return true;
                  } catch {
                    return false;
                  }
                }}
              />
            </Panel>
          );
        break;
      case 'po':
        if (inv)
          layer = (
            <Panel title="The customer’s PO" onClose={shut}>
              <PoNumber
                jobId={job.id}
                poNumber={inv.poNumber ?? null}
                required={!!job.poRequired || !!data.review?.items.some((i) => i.key === 'po')}
                canEdit={!!can.setPo && !mirroring && inv.status !== 'paid'}
                onSaved={(said) => void changed(said)}
              />
            </Panel>
          );
        break;
      case 'receipts':
        layer = (
          <Panel title="Parts bought for the job" onClose={shut}>
            <JobPurchases jobId={job.id} held={held} onChanged={changed} />
          </Panel>
        );
        break;
      case 'gallery':
        layer = (
          <Panel title={`Job ${job.number}’s photos and files`} onClose={shut}>
            <JobPhotos jobId={job.id} jobNumber={job.number} held={held} timeZone={timeZone} />
          </Panel>
        );
        break;
      case 'tasks':
        layer = (
          <Panel title={`Job ${job.number}’s tasks`} onClose={shut}>
            <TaskList
              title="This job’s tasks"
              query={{ jobId: job.id }}
              about={{ jobId: job.id }}
              timeZone={timeZone}
              canAdd={!!can.manageTasks}
              showAbout="none"
              empty="No open tasks for this job."
              onChanged={() => void load()}
            />
          </Panel>
        );
        break;
      case 'why':
        if (inv)
          layer = (
            <ConfirmDialog
              isOpen
              onClose={shut}
              title="Why didn’t the customer sign?"
              confirmLabel="Save"
              cancelLabel="Not now"
              reason={{
                label: 'Why the customer didn’t sign',
                placeholder: 'Customer wasn’t there',
                missing: 'Say why the customer didn’t sign.',
              }}
              onConfirm={(reason) =>
                run(
                  { attempt: 'why', signature: reason.trim() },
                  (idempotencyKey) =>
                    client.call(
                      'invoices.waiveAcknowledgement',
                      { invoiceId: inv.id, reason: reason.trim() },
                      { idempotencyKey },
                    ),
                  'Saved why the customer didn’t sign.',
                ).then(shut)
              }
            >
              <p>
                The job needs the customer’s signature on the work. Say why it’s missing and it can be
                completed.
              </p>
            </ConfirmDialog>
          );
        break;
      case 'text': {
        const m = data.customer?.contacts.flatMap((k) => k.methods).find((x) => x.id === over.methodId);
        if (m)
          layer = (
            <ConfirmDialog
              isOpen
              onClose={shut}
              title={`Text ${job.customerName}`}
              confirmLabel="Send the text"
              cancelLabel="Not now"
              reason={{
                label: 'The text',
                description: 'From the office line, on this job.',
                missing: 'Write the text first.',
              }}
              onConfirm={(body) =>
                run(
                  { attempt: 'text', signature: `${m.id}:${body.trim()}` },
                  (idempotencyKey) =>
                    client.call(
                      'messages.send',
                      { contactMethodId: m.id, purpose: 'general', body: body.trim(), jobId },
                      { idempotencyKey },
                    ),
                  'Text sent.',
                ).then(shut)
              }
            />
          );
        break;
      }
    }

  // Blueprint: the estimate is built on its own page over the job (EstimateBuilder, DEC-019)
  if (record && over?.kind === 'estimate-edit')
    return (
      <EstimateBuild
        job={job}
        start={over.start}
        onClose={shut}
        onSaved={(e, isNew) => done(`${isNew ? 'Saved' : 'Updated'} estimate ${e.number}.`)}
        onSend={(e) => {
          void changed(`Saved estimate ${e.number}.`);
          setOver({ kind: 'estimate-send', id: e.id, estimate: e });
        }}
      />
    );

  return (
    <>
      {record ? (
        <JobRecord
          view={record}
          siteMap={jobSiteMap(job)}
          onAction={onAction}
          openRows={openRowsIn(record, rowsOpen)}
          onOpenRows={onOpenRows}
        />
      ) : (
        <JobPage
          view={view}
          visitBlock={(v) => <JobVisitBlock v={v} />}
          siteMap={jobSiteMap(job)}
          onAction={onAction}
        />
      )}
      {layer}
    </>
  );
}
